import React, { useEffect, useLayoutEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Common Components
import { GridOverlay } from './components/Common/UI';

// Sections — eagerly loaded (above the fold)
import Header from './components/Sections/Header';
import Hero from './components/Sections/Hero';
import ProblemSection from './components/Sections/ProblemSection';
import Footer from './components/Sections/Footer';
import CookieConsent from './components/Sections/CookieConsent';
import EmailPopup from './components/Sections/EmailPopup';
import { TERMS_OF_SERVICE, PRIVACY_POLICY } from './constants/data.jsx';

// Sections — lazy loaded (below the fold)
const ComparisonSection = lazy(() => import('./components/Sections/ComparisonSection'));
const ProductAnatomySection = lazy(() => import('./components/Sections/ProductAnatomySection'));
const ProductExpertsSection = lazy(() => import('./components/Sections/ProductExpertsSection'));
const TechnologySection = lazy(() => import('./components/Sections/TechnologySection'));
const ProductFinishesSection = lazy(() => import('./components/Sections/ProductFinishesSection'));
const ReserveCTASection = lazy(() => import('./components/Sections/ReserveCTASection'));
const FAQSection = lazy(() => import('./components/Sections/FAQSection'));
const TeamSection = lazy(() => import('./components/Sections/TeamSection'));

// Pages — lazy loaded (only fetched when navigated to)
const SciencePage = lazy(() => import('./components/Sections/SciencePage'));
const LegalPage = lazy(() => import('./components/Sections/LegalPage'));
const DataCheckPage = lazy(() => import('./pages/DataCheckPage'));
const ReservationSuccessPage = lazy(() => import('./pages/ReservationSuccessPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const FoundersPage = lazy(() => import('./pages/FoundersPage'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
  }, [pathname]);
  return null;
};

const HomePage = () => {
  useEffect(() => {
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
    }
  }, []);

  return (
    <div className="bg-[#FCFBF9] text-[#2A2422] font-sans selection:bg-[#f2663b] selection:text-white antialiased relative">
      <Header />
      <Hero />
      <ProblemSection />
      <Suspense fallback={null}>
        <ComparisonSection />
        {/* <IntegratedScanner /> */}
        <ProductAnatomySection />
        <ProductFinishesSection />
        <ProductExpertsSection />
        <div className="bg-white">
           <TechnologySection />
        </div>
        <ReserveCTASection />
        <FAQSection />
        <TeamSection />
      </Suspense>
      <Footer />

      {/* Version Switcher */}
      {/* <div className="hidden md:flex" style={{position:'fixed',bottom:'24px',right:'24px',zIndex:9999,gap:'8px',background:'rgba(0,0,0,0.75)',backdropFilter:'blur(8px)',borderRadius:'99px',padding:'6px 10px',boxShadow:'0 4px 20px rgba(0,0,0,0.3)'}}>
        <span style={{color:'rgba(255,255,255,0.4)',fontSize:'11px',letterSpacing:'0.05em',alignSelf:'center',paddingLeft:'4px',paddingRight:'2px'}}>VERSION</span>
        <span style={{background:'#f2663b',color:'#fff',fontSize:'12px',fontWeight:600,padding:'4px 12px',borderRadius:'99px',letterSpacing:'0.05em'}}>DESIGNER</span>
        <a href="https://biohacker.bananawong.com" style={{color:'rgba(255,255,255,0.6)',fontSize:'12px',fontWeight:500,padding:'4px 12px',borderRadius:'99px',letterSpacing:'0.05em',textDecoration:'none',transition:'all 0.2s'}} onMouseEnter={e=>e.target.style.color='#fff'} onMouseLeave={e=>e.target.style.color='rgba(255,255,255,0.6)'}>BIOHACKER</a>
      </div> */}
    </div>
  );
};

export default function App() {
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = 'https://res.cloudinary.com/dehcd3s6m/image/upload/q_auto,f_auto/v1774254323/faviconV2_jgop1q.png';
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/science" element={<SciencePage />} />
          <Route path="/science/" element={<SciencePage />} />
          <Route path="/datacheck" element={<DataCheckPage />} />
          <Route path="/datacheck/:zip" element={<DataCheckPage />} />
          <Route path="/founders" element={<FoundersPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/reservation-success" element={<ReservationSuccessPage />} />
          <Route path="/terms" element={<LegalPage title={TERMS_OF_SERVICE.title} content={TERMS_OF_SERVICE.content} />} />
          <Route path="/privacy" element={<LegalPage title={PRIVACY_POLICY.title} content={PRIVACY_POLICY.content} />} />
        </Routes>
      </Suspense>
      <CookieConsent />
      <EmailPopup />
    </Router>
  );
}
